<script lang="ts">
  import { push } from 'svelte-spa-router';
  import Header from '../../app/layout/Header.svelte';
  import { listCategories } from '../../data/categories';
  import { getBudget, ensureCurrentMonthBudgets } from '../../data/budgets';
  import { sumExpensesByCategoryAndMonth } from '../../data/expenses';
  import type { Category } from '../../data/types';
  import { getCurrentYearMonth } from '../../lib/date';
  import { formatCurrencyJPY } from '../../lib/format';

  interface CategorySummary {
    category: Category;
    budgetAmount: number;
    spentAmount: number;
    remaining: number;
  }

  const CATEGORY_COLORS = [
    '#f97316',
    '#ec4899',
    '#8b5cf6',
    '#0ea5e9',
    '#10b981',
    '#eab308',
    '#ef4444',
    '#14b8a6',
  ];

  function categoryColor(id: string): string {
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
      hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
    }
    return CATEGORY_COLORS[hash % CATEGORY_COLORS.length];
  }

  function progressRatio(summary: CategorySummary): number {
    if (summary.budgetAmount <= 0) return 0;
    return Math.min(1, summary.spentAmount / summary.budgetAmount);
  }

  function progressColor(summary: CategorySummary): string {
    if (summary.budgetAmount <= 0) return 'var(--color-border)';
    const ratio = summary.spentAmount / summary.budgetAmount;
    if (ratio > 1) return 'var(--color-danger)';
    if (ratio >= 0.8) return 'var(--color-warning)';
    return 'var(--color-success)';
  }

  const yearMonth = getCurrentYearMonth();

  let summaries = $state<CategorySummary[]>([]);
  let loading = $state(true);
  let errorMessage = $state('');

  async function loadSummaries() {
    loading = true;
    errorMessage = '';
    try {
      await ensureCurrentMonthBudgets(yearMonth);
      const categories = await listCategories();
      const results = await Promise.all(
        categories.map(async (category) => {
          const [budget, spentAmount] = await Promise.all([
            getBudget(category.id, yearMonth),
            sumExpensesByCategoryAndMonth(category.id, yearMonth),
          ]);
          const budgetAmount = budget?.amount ?? 0;
          return {
            category,
            budgetAmount,
            spentAmount,
            remaining: budgetAmount - spentAmount,
          };
        })
      );
      summaries = results;
    } catch (err) {
      console.error(err);
      errorMessage = 'データの読み込みに失敗しました。';
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    loadSummaries();
  });

  function goToCategory(categoryId: string) {
    push('/category/' + categoryId);
  }

  function handleRowKeydown(event: KeyboardEvent, categoryId: string) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      goToCategory(categoryId);
    }
  }
</script>

<Header title="サマリー" showBack={false}>
  {#snippet right()}
    <button type="button" class="nav-button" onclick={() => push('/settings')}>設定</button>
  {/snippet}
</Header>

<main class="page">
  <nav class="top-nav">
    <button type="button" class="nav-button" onclick={() => push('/report')}>集計</button>
    <button type="button" class="nav-button" onclick={() => push('/category-mgmt')}>
      カテゴリ管理
    </button>
  </nav>

  {#if errorMessage}
    <p class="error-text">{errorMessage}</p>
  {/if}

  {#if loading}
    <p class="muted-text">読み込み中...</p>
  {:else if summaries.length === 0}
    <p class="muted-text">
      カテゴリが登録されていません。「カテゴリ管理」から追加してください。
    </p>
  {:else}
    <ul class="category-list">
      {#each summaries as summary (summary.category.id)}
        <li>
          <div
            class="category-row"
            role="button"
            tabindex="0"
            onclick={() => goToCategory(summary.category.id)}
            onkeydown={(e) => handleRowKeydown(e, summary.category.id)}
          >
            <div class="category-row__header">
              <span
                class="category-row__dot"
                style={`background-color: ${categoryColor(summary.category.id)}`}
              ></span>
              <div class="category-row__name">{summary.category.name}</div>
            </div>
            <div class="category-row__figures">
              <div class="figure">
                <span class="figure__label">予算</span>
                <span class="figure__value">{formatCurrencyJPY(summary.budgetAmount)}</span>
              </div>
              <div class="figure">
                <span class="figure__label">支出</span>
                <span class="figure__value">{formatCurrencyJPY(summary.spentAmount)}</span>
              </div>
              <div class="figure">
                <span class="figure__label">残り</span>
                <span
                  class="figure__value"
                  class:figure__value--danger={summary.remaining < 0}
                >
                  {formatCurrencyJPY(summary.remaining)}
                </span>
              </div>
            </div>
            {#if summary.budgetAmount > 0}
              <div class="progress-bar">
                <div
                  class="progress-bar__fill"
                  style={`width: ${progressRatio(summary) * 100}%; background-color: ${progressColor(summary)}`}
                ></div>
              </div>
            {/if}
          </div>
        </li>
      {/each}
    </ul>
  {/if}
</main>

<style>
  .page {
    padding: 1rem;
    max-width: 640px;
    margin: 0 auto;
  }

  .top-nav {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  .nav-button {
    padding: 0.5rem 0.9rem;
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border);
    background-color: var(--color-bg-elevated);
    color: var(--color-text);
    cursor: pointer;
    font-size: 0.9rem;
  }

  .nav-button:hover,
  .nav-button:focus-visible {
    border-color: var(--color-primary);
  }

  .muted-text {
    color: var(--color-text-muted);
  }

  .error-text {
    color: var(--color-danger);
  }

  .category-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }

  .category-row {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    padding: 0.9rem 1.1rem;
    border-radius: var(--radius-lg);
    border: 1px solid var(--color-border);
    background-color: var(--color-bg-elevated);
    box-shadow: 0 1px 2px var(--color-shadow);
    cursor: pointer;
    transition:
      transform 0.15s ease,
      box-shadow 0.15s ease,
      border-color 0.15s ease;
  }

  .category-row:hover,
  .category-row:focus-visible {
    border-color: var(--color-primary);
    outline: none;
    transform: translateY(-2px);
    box-shadow: 0 6px 14px var(--color-shadow);
  }

  .category-row:active {
    transform: scale(0.98);
  }

  .category-row__header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .category-row__dot {
    width: 0.65rem;
    height: 0.65rem;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .category-row__name {
    font-size: 1.05rem;
    font-weight: 600;
    color: var(--color-text);
  }

  .progress-bar {
    height: 6px;
    border-radius: var(--radius-sm);
    background-color: var(--color-border);
    overflow: hidden;
  }

  .progress-bar__fill {
    height: 100%;
    border-radius: inherit;
    transition:
      width 0.3s ease,
      background-color 0.3s ease;
  }

  .category-row__figures {
    display: flex;
    gap: 1.25rem;
    flex-wrap: wrap;
  }

  .figure {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  .figure__label {
    font-size: 0.75rem;
    color: var(--color-text-muted);
  }

  .figure__value {
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--color-text);
  }

  .figure__value--danger {
    color: var(--color-danger);
  }
</style>
